import React from "react";

import { Tooltip } from "@mui/material";
import clsx from "clsx";

import Icon, { type IIconName } from "@musicfy/components/Icon";
import Slider from "@musicfy/components/Slider";
import { Features } from "@musicfy/contants/Features";
import { usePaddleContext } from "@musicfy/libs/PaddleProvider";
import { useSubscriptionContext } from "@musicfy/libs/SubscriptionProvider";
import { canUseFeature } from "@musicfy/utils";

import { type TInferenceSettings } from "../types";

interface IProcessingEffectsProps {
  inferenceSettings: TInferenceSettings;
  setInferenceSettings: React.Dispatch<
    React.SetStateAction<TInferenceSettings>
  >;
}

type ProcessingToggleKey =
  | "shouldIsolateVocals"
  | "shouldEnhanceVocals"
  | "cleanAudio";

interface ProcessingOption {
  label: string;
  icons: {
    active: IIconName;
    inactive: IIconName;
  };
  key: ProcessingToggleKey;
  isPremium: boolean;
}

const PROCESSING_OPTIONS: ProcessingOption[] = [
  {
    label: "Remove Instrumentals",
    icons: {
      active: "separate",
      inactive: "separate-white",
    },
    key: "shouldIsolateVocals",
    isPremium: false,
  },
  {
    label: "Remove Reverb/Echo",
    icons: {
      active: "enhance",
      inactive: "enhance-white",
    },
    key: "shouldEnhanceVocals",
    isPremium: false,
  },
  {
    label: "Reduce Background Noise",
    icons: {
      active: "sound",
      inactive: "sound",
    },
    key: "cleanAudio",
    isPremium: false,
  },
];

const ProcessingEffects = ({
  inferenceSettings,
  setInferenceSettings,
}: IProcessingEffectsProps) => {
  const { openProUpsell } = usePaddleContext();
  const { subscription, isLoading: isSubscriptionLoading } =
    useSubscriptionContext();

  const canUsePremiumSettings = canUseFeature(
    Features.MASTER_QUALITY,
    subscription
  );

  return (
    <div className="w-full">
      <div className="mt-4 grid grid-cols-2 gap-6">
        {PROCESSING_OPTIONS.map(({ label, key, icons, isPremium }) => {
          const isActive = inferenceSettings[key];
          return (
            <div
              key={key}
              role="button"
              aria-label={`${isActive ? "Toggle Off" : "Toggle On"} ${label}`}
              tabIndex={0}
              onClick={() => {
                // Subscription still loading -> don't misclassify an eligible
                // payer as ineligible and show them the upgrade modal.
                if (isPremium && isSubscriptionLoading) {
                  return;
                }
                if (isPremium && !canUsePremiumSettings) {
                  openProUpsell("premium-advanced-settings");
                  return;
                }

                setInferenceSettings((prev) => {
                  return {
                    ...prev,
                    [key]: !prev[key],
                  };
                });
              }}
              className={clsx(
                "transition-all group outline outline-1 bg-white/5 relative overflow-hidden cursor-pointer rounded-8 flex items-center justify-center py-6 lg:px-16 px-6",
                {
                  "outline-white/15 xl:hover:outline-white/30": !isActive,
                  "outline-pink-200": isActive,
                }
              )}
            >
              <div className="flex lg:flex-row flex-col items-center gap-2">
                <Icon
                  name={isActive ? icons.active : icons.inactive}
                  className="pb-[2px]"
                />
                <div className="lg:text-left text-center">{label}</div>
              </div>
              {isPremium && (
                <Icon name="crown" className="absolute top-3 right-3" />
              )}
            </div>
          );
        })}
      </div>

      {inferenceSettings.cleanAudio && (
        <div className="mt-4 p-5 flex lg:flex-row flex-col lg:items-center rounded-12 bg-white/2 border border-solid border-white/25">
          <Tooltip
            arrow
            title="Higher values remove more background noise. Too high can dull clean vocals."
          >
            <div className="flex items-center gap-2 cursor-pointer">
              <Icon name="help" className="pb-[2px]" />
              <div className="whitespace-nowrap">Noise reduction strength</div>
            </div>
          </Tooltip>
          <div className="w-full lg:ml-6 gap-6 flex items-center justify-center">
            <div className="text-white/60 text-14">Light</div>
            <Slider
              value={inferenceSettings.cleanStrength}
              step={0.05}
              min={0}
              max={1}
              size="small"
              onChange={(_, val) => {
                setInferenceSettings((prev) => ({
                  ...prev,
                  cleanStrength: val as number,
                }));
              }}
            />
            <div className="text-white/60 text-14">Strong</div>
            <div className="min-w-[3ch] text-right tabular-nums">
              {inferenceSettings.cleanStrength.toFixed(2)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessingEffects;
